import React, { Fragment } from "react";
import { withRouter } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import Swal from "sweetalert2";

import FormInput from "../components/forminput";
import FormTextArea from "../components/formtextarea";

//Tope de caracteres del mensaje informativo: es lo que entra en el bloque
//de 350x350 del home sin desbordarlo (ver .infobox en App.css).
const MAX_MSJ_INFO = 60;

//Nombres legibles de las etapas, para los mensajes de confirmación.
const NOMBRES_ETAPA = {
  informativa: "Informativa",
  presentacion: "Presentación",
  votacion: "Votación",
  seleccion: "Selección",
};

const AdminEtapa = (props) => {
  const municipio = props.municipio;
  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      etapa: municipio.etapa,
      tituloHome: municipio.tituloHome,
      msjHome: municipio.msjHome,
      msjProp: municipio.msjProp,
      msjInfo: municipio.msjInfo || "",
    },
  });

  //El mensaje de la etapa informativa solo se edita cuando esa es la etapa elegida.
  const etapaElegida = watch("etapa");
  const msjInfoActual = watch("msjInfo") || "";

  //Persiste los cambios del municipio. Si la nueva etapa es "votacion" se crea
  //la votación tomando las propuestas aceptadas.
  const guardar = (data) => {
    axios
      .put(`api/municipios/${municipio._id}`, data)
      .then((res) => {
        //Redireccionar
        const municipioRes = res.data.municipioActualizado;
        if (municipioRes.etapa === "votacion") {
          axios.post(`api/votaciones/${municipio._id}`);
        }
        Swal.fire("Se actualizó con éxito la información", "", "success");
        props.history.push(`/consola`);
      })
      .catch((err) => {
        Swal.fire(
          err.response && err.response.data && err.response.data.errorMessage
            ? err.response.data.errorMessage
            : "Ocurrió un error, por favor inténtelo de nuevo o pónganse en contacto.",
          "",
          "error"
        );
      });
  };

  //Cambiar de etapa es la acción más consecuente de la consola: antes de
  //hacerlo se pide confirmación explicando qué efecto tiene según el destino.
  const confirmarCambioEtapa = async (destino) => {
    let html;
    if (destino === "presentacion") {
      html =
        "Se abrirá la <b>presentación de propuestas</b>: los vecinos van a poder enviar sus propuestas desde la página del municipio.";
    } else if (destino === "votacion") {
      //Se consulta cuántas propuestas aceptadas entrarán en la votación.
      let aceptadas = null;
      try {
        const res = await axios.get(
          `api/propuestas?municipioId=${municipio._id}&estado=aceptada`
        );
        aceptadas = res.data.propuestas.length;
      } catch (e) {
        //Si no se puede obtener el número, se confirma igual sin él.
      }
      const cuenta =
        aceptadas !== null
          ? `las <b>${aceptadas} propuesta${
              aceptadas === 1 ? "" : "s"
            } aceptada${aceptadas === 1 ? "" : "s"}</b>`
          : "las propuestas aceptadas";
      html =
        `Se va a <b>crear la votación</b> tomando ${cuenta} y los vecinos van a poder empezar a votar.` +
        (aceptadas !== null && aceptadas < 2
          ? "<br><br><b>Atención:</b> se necesitan al menos 2 propuestas aceptadas para abrir la votación."
          : "");
    } else if (destino === "seleccion") {
      html =
        "Se publicarán las <b>propuestas seleccionadas</b> como ganadoras y el proceso quedará cerrado. <b>No vas a poder volver a una etapa anterior.</b>";
    } else {
      html =
        "La etapa informativa funciona como una <b>pausa</b>: cierra para los " +
        "vecinos la presentación o la votación en curso y la página del " +
        "municipio vuelve a mostrar solo <b>información general</b>. " +
        "<b>Los datos de la votación y la fiscalización se conservan</b> y " +
        "podés seguir viéndolos desde la consola (por ejemplo, para analizar " +
        "las propuestas o fiscalizar los votos).";
    }

    const result = await Swal.fire({
      title: `¿Cambiar a la etapa "${NOMBRES_ETAPA[destino]}"?`,
      html,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cambiar de etapa",
      cancelButtonText: "Cancelar",
    });
    return result.isConfirmed;
  };

  const onSubmit = async (data) => {
    //Si no se está en la etapa informativa, no se toca el mensaje guardado.
    if (etapaElegida !== "informativa") {
      delete data.msjInfo;
    }
    //Solo se confirma cuando la etapa realmente cambia; editar los mensajes del
    //municipio sin cambiar de etapa guarda directamente.
    if (data.etapa !== municipio.etapa) {
      const confirmado = await confirmarCambioEtapa(data.etapa);
      if (!confirmado) return;
    }
    guardar(data);
  };

  if (!municipio) return null;

  return (
    <Fragment>
      <div className="row">
        <div className="col-md-12">
          <h2 className="ppr-titulo centrado my-2 h4">Administrar Etapa</h2>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 mx-auto">
          <form className="m-3" onSubmit={handleSubmit(onSubmit)}>
            <div className="from-group mb-3">
              <label htmlFor="etapa">Etapa:</label>
              <select
                {...register("etapa")}
                className="form-control"
                id="etapa"
              >
                <option value="informativa">Informativa</option>
                <option value="presentacion">Presentación</option>
                <option value="votacion">Votación</option>
                <option value="seleccion">Selección</option>
              </select>
            </div>

            {etapaElegida === "informativa" && (
              <div className="form-group">
                <label htmlFor="msjInfo">
                  Mensaje Destacado de la Etapa Informativa:
                </label>
                <textarea
                  {...register("msjInfo", { maxLength: MAX_MSJ_INFO })}
                  className="form-control"
                  id="msjInfo"
                  rows={2}
                  maxLength={MAX_MSJ_INFO}
                  placeholder="¡Pronto tendremos novedades!"
                />
                <small className="form-text text-muted">
                  {msjInfoActual.length}/{MAX_MSJ_INFO} caracteres. Es el título
                  grande del inicio: si se deja vacío se muestra "¡Pronto
                  tendremos novedades!".
                </small>
                {errors.msjInfo && (
                  <small className="text-danger form-text">
                    El mensaje no puede superar los {MAX_MSJ_INFO} caracteres.
                  </small>
                )}
              </div>
            )}

            <FormInput
              register={register}
              error={errors.tituloHome}
              label={"Título :"}
              id={"tituloHome"}
              isRequired={true}
              placeholder="Título del mensaje en la página de inicio."
            />

            <FormTextArea
              register={register}
              error={errors.msjHome}
              label={"Mensaje :"}
              id={"msjHome"}
              isRequired={true}
              placeholder="Mensaje en la página de inicio."
              rows={4}
            />

            <FormTextArea
              register={register}
              error={errors.msjProp}
              label={"Mensaje Para Todas Las Propuestas:"}
              id={"msjProp"}
              isRequired={false}
              placeholder="Mensaje que se publica en todas las páginas de las propuestas."
              rows={4}
            />

            <div className="text-center">
              <button
                type="submit"
                className="btn ppr-btn-azul font-weight-bold m-2 mr-5"
              >
                Guardar Cambios
              </button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default withRouter(AdminEtapa);
